import {
  Button,
  column,
  Css,
  dateColumn,
  GridColumn,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  useModal,
} from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { dateCell } from "src/components";
import {
  TakeoffsUsageModalQuery,
  useTakeoffsUsageModalQuery,
} from "src/routes/libraries/material-catalog/components/takeoffs-usage-modal/TakeoffsUsageModal.generated";
import { createPlanPackageTakeoffUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";

type TakeoffsUsageModalProps = { materialVariantId: string };

type TakeoffUsageReadyPlan = TakeoffsUsageModalQuery["materialVariant"]["takeoffUsages"][number];
type Row = SimpleHeaderAndData<TakeoffUsageReadyPlan>;

export const takeoffsUsageModalOptions: Pick<ModalProps, "size" | "drawHeaderBorder"> = {
  size: "lg",
  drawHeaderBorder: true,
};

export function TakeoffsUsageModal(props: TakeoffsUsageModalProps) {
  const { materialVariantId } = props;
  const { closeModal } = useModal();
  const query = useTakeoffsUsageModalQuery({ variables: { id: materialVariantId } });

  return (
    <>
      <ModalHeader>Usage Details</ModalHeader>
      <ModalBody>
        {queryResult(query, {
          data: (data) => <TakeoffsUsageList takeoffUsages={data.materialVariant.takeoffUsages} />,
        })}
      </ModalBody>
      <ModalFooter>
        <Button label="Ok" onClick={closeModal} />
      </ModalFooter>
    </>
  );
}

type TakeoffsUsageListProps = { takeoffUsages: TakeoffsUsageModalQuery["materialVariant"]["takeoffUsages"] };

function TakeoffsUsageList(props: TakeoffsUsageListProps) {
  const { takeoffUsages } = props;
  const sections = useMemo(
    () =>
      [
        {
          title: "Takeoff",
          testId: "planPackages",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "PlanPackage")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
        {
          title: "Design Package",
          testId: "designPackages",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "DesignPackage")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
        {
          title: "Product Offering",
          testId: "productOfferings",
          items: takeoffUsages
            .filter((rp) => rp.__typename === "ProductOffering")
            .sortBy((rp) => rp.displayName ?? rp.name),
        },
      ].filter((section) => section.items.nonEmpty),
    [takeoffUsages],
  );

  if (sections.isEmpty) {
    return <div css={Css.gray700.$}>Not used in any takeoffs.</div>;
  }

  return (
    <div data-testid="takeoffsUsageList">
      <div css={Css.gray600.sm.my3.$}>This material is used in the following locations:</div>
      <div css={Css.df.fdc.gap4.$}>
        {sections.map((section) => (
          <TakeoffsUsageSection
            key={section.testId}
            title={section.title}
            testId={section.testId}
            items={section.items}
          />
        ))}
      </div>
    </div>
  );
}

type TakeoffsUsageSectionProps = { title: string; testId: string; items: TakeoffUsageReadyPlan[] };

function TakeoffsUsageSection(props: TakeoffsUsageSectionProps) {
  const { title, testId, items } = props;
  return (
    <div data-testid={testId}>
      <div css={Css.lg.fw6.mb2.$}>{`${items.length} ${title}${items.length === 1 ? "" : "s"}`}</div>
      <GridTable
        columns={columns}
        rows={[simpleHeader, ...simpleDataRows(items)]}
        style={{ allWhite: true, bordered: true, rowHeight: "fixed", rowHover: false }}
      />
    </div>
  );
}

type TakeoffUsageLinkProps = { readyPlan: TakeoffUsageReadyPlan };

function TakeoffUsageLink(props: TakeoffUsageLinkProps) {
  const { readyPlan } = props;
  const label = readyPlan.displayName ?? readyPlan.name;
  const to = getTakeoffUsageUrl(readyPlan);
  if (!to) {
    return <span>{label}</span>;
  }
  return (
    <Link css={Css.blue700.$} data-testid="takeoffsUsageItem" to={to} target="_blank">
      {label}
    </Link>
  );
}

const columns: GridColumn<Row>[] = [
  column<Row>({
    header: "Name",
    data: (readyPlan) => ({
      content: () => <TakeoffUsageLink readyPlan={readyPlan} />,
      value: readyPlan.displayName ?? readyPlan.name,
    }),
  }),
  column<Row>({ header: "Owner", data: ({ owner }) => owner?.name }),
  dateColumn<Row>({ header: "Last Edited", data: ({ updatedAt }) => dateCell(updatedAt, { dateFormatStyle: "long" }) }),
];

function getTakeoffUsageUrl(readyPlan: TakeoffUsageReadyPlan): string | undefined {
  if (readyPlan.__typename === "PlanPackage") {
    // Plan Packages also have a blueprintURL; but it takes you to the plan overview
    // This will take the user straight to the takeoff manager to reduce an extra click
    return createPlanPackageTakeoffUrl(readyPlan.id, readyPlan.aggregateLatest.id);
  }
  if (readyPlan.__typename === "DesignPackage" || readyPlan.__typename === "ProductOffering") {
    return readyPlan.blueprintUrl.path;
  }
  return undefined;
}
